import React from 'react';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../../components/ui/table';
import { Button } from '../../../components/ui/button';
import { Switch } from '../../../components/ui/switch';
import { Tooltip } from '../../../components/ui/tooltip';
import { Pagination } from '../../../components/ui/Pagination';
import { FileText, GitPullRequestArrow, Settings } from 'lucide-react';
import { getSequenceColumns, generateSequenceData } from '../helpers/sequenceHelpers';
import { commonConstants } from '../constants/commonConstants';
import type { SequenceTableRow } from '../types';
import PipeSequence from '../../../components/PipeSequence/PipeSequence';

const SEQUENCE_TABLE_PAGE_SIZE_OPTIONS = [25, 50, 100, 200, 300];
const PAGINATION_THRESHOLD = commonConstants.clientPaginationThreshold;

function shouldPaginate(itemCount: number): boolean {
  return itemCount > PAGINATION_THRESHOLD;
}

function clampSequencePageSize(n: number): number {
  return Math.min(300, Math.max(1, Math.floor(n)));
}

interface SequenceDataProps {
  sequenceList: any[];
  selectionType: string;
  spreadName: string;
  currentPage: number;
  rowsPerPage: number;
  totalCount: number;
  handleTableChange: (pagination: { current: number; pageSize: number }) => void;
  canEdit?: boolean;
  canReport?: boolean;
  hasSelectedSequence?: boolean;
  onOpenSequenceReport?: () => void;
  onOpenUpdateSequence?: () => void;
  onOpenSequenceSettings?: () => void;
  sequenceViewMode: 'graph' | 'table';
  onSequenceViewModeChange: (mode: 'graph' | 'table') => void;
  graphRecordList: any[];
  graphPipesPerRow: number;
  onGraphPipesPerRowChange: (pipesPerRow: number) => void;
  isGraphPanelLoading: boolean;
  sequenceLoadingMore?: boolean;
  graphFetchError: string | null;
  onRetryGraphFetch: () => void;
  defaultOD?: number;
}

const SequenceData: React.FC<SequenceDataProps> = ({
  sequenceList,
  selectionType,
  spreadName,
  currentPage,
  rowsPerPage,
  totalCount,
  handleTableChange,
  canEdit = true,
  canReport = true,
  hasSelectedSequence = false,
  onOpenSequenceReport,
  onOpenUpdateSequence,
  onOpenSequenceSettings,
  sequenceViewMode,
  onSequenceViewModeChange,
  graphRecordList,
  graphPipesPerRow,
  onGraphPipesPerRowChange,
  isGraphPanelLoading,
  sequenceLoadingMore = false,
  graphFetchError,
  onRetryGraphFetch,
  defaultOD,
}) => {
  const columns = getSequenceColumns(selectionType);
  const data = generateSequenceData(sequenceList, spreadName) as SequenceTableRow[];
  const loadedCount = data.length;
  const paginateTable = shouldPaginate(loadedCount);
  const startIndex = paginateTable ? (currentPage - 1) * rowsPerPage : 0;
  const paginatedData = (paginateTable
    ? data.slice(startIndex, startIndex + rowsPerPage)
    : data
  ).map((row, idx) => ({
    ...row,
    slNo: startIndex + idx + 1,
  }));
  const graphLoadedCount = graphRecordList.length;
  const paginateGraph = shouldPaginate(graphLoadedCount);
  const graphStartIndex = paginateGraph ? (currentPage - 1) * rowsPerPage : 0;
  const paginatedGraphRecords = paginateGraph
    ? graphRecordList.slice(graphStartIndex, graphStartIndex + rowsPerPage)
    : graphRecordList;

  // Add tooltip to last column
  if (columns.length > 0) {
    const lastColumn = columns[columns.length - 1];
    if (lastColumn.dataIndex === 'tdc') {
      lastColumn.title = (
        <span>
          {lastColumn.title}
          <Tooltip content="Distance (in mm) measured around the pipe from the seam center.
Positive values = clockwise direction
Negative values = anticlockwise direction (viewed from “PipeStart” end).
">
            <sup className="cursor-help ml-1">ⓘ</sup>
          </Tooltip>
        </span>
      );
    }
  }

  const handlePageSizeChange = (itemsPerPage: number) => {
    handleTableChange({
      current: 1,
      pageSize: clampSequencePageSize(itemsPerPage),
    });
  };

  const handlePageChange = (page: number) => {
    handleTableChange({
      current: page,
      pageSize: rowsPerPage,
    });
  };

  const buildRangeLabel = (count: number, start: number, pageSize: number) =>
    count === 0
      ? 'No sequence rows loaded'
      : `Showing ${start + 1}-${Math.min(start + (shouldPaginate(count) ? pageSize : count), count)} of ${count}${
          sequenceLoadingMore && totalCount > count ? ` (loading ${totalCount}…)` : ''
        }`;

  const rangeLabel = buildRangeLabel(loadedCount, startIndex, rowsPerPage);
  const graphRangeLabel = buildRangeLabel(graphLoadedCount, graphStartIndex, rowsPerPage);

  const paginationControls =
    paginateTable ? (
      <Pagination
        currentPage={currentPage}
        totalItems={loadedCount}
        itemsPerPage={rowsPerPage}
        onPageChange={handlePageChange}
        onItemsPerPageChange={handlePageSizeChange}
        pageSizeOptions={SEQUENCE_TABLE_PAGE_SIZE_OPTIONS}
        showPageNumbers
        maxVisiblePages={5}
      />
    ) : null;

  const graphPaginationControls =
    paginateGraph ? (
      <Pagination
        currentPage={currentPage}
        totalItems={graphLoadedCount}
        itemsPerPage={rowsPerPage}
        onPageChange={handlePageChange}
        onItemsPerPageChange={handlePageSizeChange}
        pageSizeOptions={SEQUENCE_TABLE_PAGE_SIZE_OPTIONS}
        showPageNumbers
        maxVisiblePages={5}
      />
    ) : null;

  const toolbar = (
    <div className="flex w-full flex-wrap items-center justify-between gap-3 gap-y-3">
      <div className="flex flex-wrap items-center gap-2" />
      <div className="flex min-w-0 flex-wrap items-center justify-end gap-2">
        <div
          className="flex flex-wrap items-center gap-2 mr-2"
          role="group"
          aria-label="Sequence view mode"
        >
          <span
            className={`flex items-center gap-1 text-sm font-medium ${sequenceViewMode === 'table' ? 'text-foreground' : 'text-muted-foreground'}`}
          >
            Table
          </span>
          <Switch
            checked={sequenceViewMode === 'graph'}
            onCheckedChange={(checked: boolean) =>
              onSequenceViewModeChange(checked ? 'graph' : 'table')
            }
            aria-label="Toggle between table and graph view"
          />
          <span
            className={`flex items-center gap-1 text-sm font-medium ${sequenceViewMode === 'graph' ? 'text-foreground' : 'text-muted-foreground'}`}
          >
            Graph
          </span>
        </div>
        <Button
          size="sm"
          className="flex items-center gap-1"
          onClick={onOpenSequenceReport}
          disabled={!canReport || !hasSelectedSequence}
        >
          <FileText className="h-4 w-4" />
          <span>Sequence report</span>
        </Button>
        <Button
          size="sm"
          className="flex items-center gap-1"
          onClick={onOpenUpdateSequence}
          disabled={!canEdit || !hasSelectedSequence}
        >
          <GitPullRequestArrow className="h-4 w-4" />
          <span>Update Sequence</span>
        </Button>
        <Button
          size="sm"
          className="flex items-center gap-1"
          onClick={onOpenSequenceSettings}
          disabled={!canEdit || !hasSelectedSequence}
        >
          <Settings className="h-4 w-4" />
          <span>Sequence Settings</span>
        </Button>
      </div>
    </div>
  );

  if (sequenceViewMode === 'graph') {
    return (
      <div className="space-y-4">
        {toolbar}
        {isGraphPanelLoading ? (
          <div className="flex min-h-[280px] flex-col items-center justify-center gap-3 rounded-lg border border-border bg-muted/20">
            <div className="h-8 w-8 animate-spin rounded-full border-2 border-[#EB1C37] border-t-transparent" />
            <span className="text-sm text-muted-foreground">Loading sequence graph…</span>
          </div>
        ) : graphFetchError ? (
          <div className="rounded-lg border border-destructive/30 bg-destructive/5 p-6 text-center">
            <p className="text-sm text-destructive mb-3">{graphFetchError}</p>
            <Button type="button" variant="outline" size="sm" onClick={onRetryGraphFetch}>
              Retry
            </Button>
          </div>
        ) : graphLoadedCount === 0 ? (
          <div className="p-6 text-center text-gray-500">No sequence data available</div>
        ) : (
          <>
            <div className="flex items-center justify-end">
              <div className="text-sm text-gray-600">{graphRangeLabel}</div>
            </div>
            <div className="overflow-x-auto h-[600px]">
              <PipeSequence
                apiResponse={{ data: { recordList: paginatedGraphRecords } }}
                defaultOD={defaultOD}
                pipesPerRow={graphPipesPerRow}
              />
            </div>
            {graphPaginationControls}
          </>
        )}
      </div>
    );
  }

  if (!data || data.length === 0) {
    return (
      <div className="space-y-4">
        {toolbar}
        <div className="p-6 text-center text-gray-500">No sequence data available</div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {toolbar}
      <div className="flex items-center justify-end">
        <div className="text-sm text-gray-600">{rangeLabel}</div>
      </div>

      <div className="border rounded-lg overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow>
              {columns.map((col: any, idx: number) => (
                <TableHead key={idx}>{col.title}</TableHead>
              ))}
            </TableRow>
          </TableHeader>
          <TableBody>
            {paginatedData.map((row: SequenceTableRow, idx: number) => (
              <TableRow key={row.key ?? idx}>
                {columns.map((col: any, colIdx: number) => (
                  <TableCell key={colIdx}>{row[col.dataIndex as keyof SequenceTableRow]}</TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      {paginationControls}
    </div>
  );
};

export default SequenceData;
